/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, Logger, OnModuleInit } from '@nestjs/common';
import { DiscoveryService, MetadataScanner, Reflector } from '@nestjs/core';
import { InstanceWrapper } from '@nestjs/core/injector/instance-wrapper';
import { TriggerClient } from '@/client/trigger-client';
import { AUTOMATION_METADATA_KEY } from '@/decorators/automation.decorator';
import { BIND_ACTION_METADATA_KEY, BindActionMetadata } from '@/decorators/bind-trigger.decorator';

/**
 * AutomationDiscoveryService
 * 
 * 自动化任务发现服务，负责扫描并注册所有标记了 @Automation 和 @BindTrigger 的任务
 */
@Injectable()
export class AutomationDiscoveryService implements OnModuleInit {
  private readonly logger = new Logger(AutomationDiscoveryService.name);

  constructor(
    private readonly discoveryService: DiscoveryService,
    private readonly metadataScanner: MetadataScanner,
    private readonly reflector: Reflector,
    private readonly triggerClient: TriggerClient,
  ) {}

  /**
   * 模块初始化时触发任务发现
   */
  async onModuleInit() {
    await this.discover();
  }

  /**
   * 发现并注册所有自动化任务
   * 
   * 扫描流程：
   * 1. 获取所有 providers 和 controllers
   * 2. 筛选出标记了 @Automation 的类
   * 3. 扫描类中标记了 @BindTrigger 的方法
   * 4. 将任务注册到 TriggerClient
   */
  private async discover(): Promise<void> {
    this.logger.debug('Starting automation trigger discovery...');

    const providers = this.discoveryService.getProviders();
    const controllers = this.discoveryService.getControllers();

    const allWrappers = [...providers, ...controllers];
    let registeredCount = 0;

    for (const wrapper of allWrappers) {
      const { instance } = wrapper;

      if (!instance || typeof instance !== 'object') {
        continue;
      }

      const isAutomationController = this.reflector.get<boolean>(
        AUTOMATION_METADATA_KEY,
        instance.constructor,
      );

      if (!isAutomationController) {
        continue;
      }

      this.logger.debug(`Found automation controller: ${instance.constructor.name}`);

      const methodNames = this.metadataScanner.getAllMethodNames(
        Object.getPrototypeOf(instance),
      );

      for (const methodName of methodNames) {
        const method = (instance as any)[methodName];
        if (typeof method !== 'function') continue;

        const actionMetadata = this.reflector.get<BindActionMetadata>(
          BIND_ACTION_METADATA_KEY,
          method,
        );
        if (!actionMetadata) continue;

        await this.registerAction(instance, methodName, method, actionMetadata, wrapper);
        registeredCount++;
      }
    }

    this.logger.debug(
      `Automation trigger discovery completed. Registered ${registeredCount} triggers.`,
    );
  }

  /**
   * 注册单个自动化任务
   * 
   * @param instance - 任务实例
   * @param methodName - 方法名称
   * @param method - 方法引用
   * @param metadata - 任务元数据
   * @param _wrapper - 实例包装器
   */
  private async registerAction(
    instance: any,
    methodName: string,
    method: Function,
    metadata: BindActionMetadata,
    _wrapper: InstanceWrapper,
  ): Promise<void> {
    const { trigger, actionName, actionDesc } = metadata;

    const name = actionName || `${instance.constructor.name}.${methodName}`;
    const actionPath = `${instance.constructor.name}:${methodName}`;

    this.logger.debug(`Registering action: ${trigger} -> ${name} (${actionPath})`);


    const handler = async (...args: any[]) => {
      this.logger.debug(`Executing action: ${trigger} with args:`, args);
      return await method.apply(instance, args);
    };

    this.triggerClient.registerAction(trigger, {
      name,
      handler,
      path: actionPath,
      desc: actionDesc,
    });

    this.logger.debug(`Action registered successfully: ${trigger}`);
  }
}
